import React from 'react';
import { FOR_RENT_SEARCH_TYPE } from '@/search/useSearchListings/useSearchListings';
import { Button, Icons, Popover, Text, Stack, styled } from '@/theme/index';
import { Nullable } from '@/utils/helperTypes';
import { useScreenSize } from '@/utils/useScreenSize';

export type ListingBedsAndBathsFilterProps = {
  currentSearchType: string;
  buttonType: 'beds' | 'baths';
  setSearchParams: (
    arg: (searchParams: URLSearchParams) => URLSearchParams
  ) => void;
} & Nullable<{
  numOfBeds?: number;
  numOfBaths?: number;
}>;

const ModalButton = styled(Button)(({ theme }) => ({
  position: 'relative',
  minWidth: '108px',
  height: '40px',
  marginRight: theme.spacing(2),
  padding: theme.spacing(1),
  justifyContent: 'space-between',
  whiteSpace: 'nowrap',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  minWidth: 'initial',
  width: '40px',
  height: '40px',
  padding: theme.spacing(0.5),
}));

const ClearButton = styled(Button)(({ theme }) => ({
  minWidth: 'initial',
  padding: theme.spacing(0),
  textDecoration: 'underline',
}));

const StyledPopover = styled(Popover)(({ theme }) => ({
  borderRadius: '8px',
  minHeight: '180px',
  padding: theme.spacing(2.625),
}));

const TopStack = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(2.625),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

/**
 * This component is responsible for rendering a beds or bath filter on the Listings search page.
 * It renders a button and popover in desktop view or a row of options in mobile view.
 * Either a beds or bath filter is rendered based on props.
 */
export function ListingBedsAndBathsFilter({
  currentSearchType,
  buttonType,
  numOfBeds,
  numOfBaths,
  setSearchParams,
}: ListingBedsAndBathsFilterProps) {
  const currentFilter =
    buttonType === 'beds'
      ? currentSearchType === FOR_RENT_SEARCH_TYPE
        ? 'rentals_bedrooms'
        : 'bedrooms'
      : currentSearchType === FOR_RENT_SEARCH_TYPE
      ? 'rentals_bathrooms'
      : 'bathrooms';

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const { isDesktop } = useScreenSize();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    setSearchParams((searchParams) => {
      searchParams.delete(currentFilter);
      setAnchorEl(null);

      return searchParams;
    });
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const bedsModalButtonText = (numOfBeds: number | null) => {
    return numOfBeds ? `Beds: ${numOfBeds}+` : 'Beds';
  };
  const bathsModalButtonText = (numOfBaths: number | null) => {
    return numOfBaths ? `Baths: ${numOfBaths}+` : 'Baths';
  };
  const buttonText = (value: string, index: number) => {
    const secondaryText = index !== 0 ? '+' : '';
    return `${value}${secondaryText}`;
  };
  const filterSelected = (
    currentFilterValue: number | null,
    value: string,
    index: number
  ) => {
    const anyFilterSelected = currentFilterValue === null && index === 0;
    const filterSelected = value === currentFilterValue?.toString();
    return anyFilterSelected || filterSelected;
  };
  const buttonIcon = () => {
    if (modalButtonText === 'Beds' || modalButtonText === 'Baths') {
      if (open) {
        return (
          <Icons.KeyboardArrowDown
            sx={{
              transition: 'all 0.2s ease-in-out',
              transform: 'rotate(-180deg)',
            }}
          />
        );
      }
      return (
        <Icons.KeyboardArrowDown
          sx={{
            transition: 'all 0.2s ease-in-out',
          }}
        />
      );
    }
    return (
      <Button
        variant="text"
        sx={{ minWidth: 'initial' }}
        onClick={handleClear}
        color="success"
      >
        <Icons.Cancel sx={{ width: '18px', height: '24px' }} />
      </Button>
    );
  };

  const open = Boolean(anchorEl);
  const modalButtonText =
    buttonType === 'beds'
      ? bedsModalButtonText(numOfBeds || null)
      : bathsModalButtonText(numOfBaths || null);
  const modalTitleText = buttonType === 'beds' ? 'Bedrooms' : 'Bathrooms';
  const bedsAndBathsButtonsValues = ['Any', '1', '2', '3', '4', '5'];

  const clickHandler = (value: string) => () => {
    setSearchParams((searchParams) => {
      if (value === 'Any') {
        searchParams.delete(currentFilter);
      } else {
        searchParams.set(currentFilter, value);
      }
      return searchParams;
    });
  };

  return (
    <>
      {isDesktop && (
        <ModalButton variant="outlined" color="primary" onClick={handleClick}>
          <Text>{modalButtonText}</Text>
          {buttonIcon()}
        </ModalButton>
      )}
      <StyledPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Stack>
          <TopStack spacing={1}>
            <Text>{modalTitleText}</Text>
            <Stack direction="row" spacing={1}>
              {bedsAndBathsButtonsValues.map((value, index) => {
                return (
                  <StyledButton
                    key={index}
                    variant={
                      filterSelected(
                        numOfBaths || numOfBeds || null,
                        value,
                        index
                      )
                        ? 'contained'
                        : 'outlined'
                    }
                    onClick={clickHandler(value)}
                    color={
                      filterSelected(
                        numOfBaths || numOfBeds || null,
                        value,
                        index
                      )
                        ? 'success'
                        : 'primary'
                    }
                  >
                    {buttonText(value, index)}
                  </StyledButton>
                );
              })}
            </Stack>
          </TopStack>
          <Stack
            justifyContent="space-between"
            direction="row"
            sx={(theme) => ({ padding: theme.spacing(1, 2.625, 2) })}
          >
            <ClearButton onClick={handleClear}>Clear</ClearButton>
            <Button
              onClick={() => {
                setAnchorEl(null);
              }}
              variant="contained"
              color="success"
            >
              Done
            </Button>
          </Stack>
        </Stack>
      </StyledPopover>
      {!isDesktop && (
        <Stack
          sx={(theme) => ({
            borderTop: `1px solid ${theme.palette.divider}`,
            padding: theme.spacing(2, 0),
          })}
        >
          <Text
            fontWeight="700"
            sx={(theme) => ({ paddingBottom: theme.spacing(2) })}
          >
            {modalTitleText}
          </Text>
          <Stack direction="row" spacing={1}>
            {bedsAndBathsButtonsValues.map((value, index) => {
              return (
                <StyledButton
                  key={index}
                  onClick={clickHandler(value)}
                  variant={
                    filterSelected(
                      numOfBaths || numOfBeds || null,
                      value,
                      index
                    )
                      ? 'contained'
                      : 'outlined'
                  }
                  color={
                    filterSelected(
                      numOfBaths || numOfBeds || null,
                      value,
                      index
                    )
                      ? 'success'
                      : 'primary'
                  }
                >
                  {buttonText(value, index)}
                </StyledButton>
              );
            })}
          </Stack>
        </Stack>
      )}
    </>
  );
}
